import React, { createRef, useState } from "react";
import {
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  AlignHorizontalRight,
  CenterFocusStrong,
} from "@mui/icons-material";
import { GridSizeSelectors } from "./GridSizeSelectors";
import { EditContainerPanel } from "./EditContainerPanel";
import { AddBlockPanel } from "./AddBlockPanel";
import { DuplicateContainerButton } from "./DuplicateContainerButton";
import { useIsActiveContainer } from "./useIsActiveContainer";
import {
  SET_ACTIVE_ELEMENT,
  useScrollytellDispatch,
  useScrollytellState,
  useScrollyTellProps,
  useScrollytellTranslation,
} from "../Admin/scrollytellContext";
import { addBlock, updateContainer, updateSection } from "../Admin/service";
import { SectionGridSize, SectionTemplates } from "../helpers/enums";
import { RemoveContainerButton } from "./RemoveContainerButton";
import { applyStickyCardPlacement } from "../Admin/templatePresets";
import { ImageFocusZoomPicker } from "../common/ImageFocusZoomPicker";
import { AdminIconBar } from "../icon-bar/AdminIconBar";
import { AdminIconBarItem } from "../icon-bar/AdminIconBarItem";

export const EditStickyBlockContainer = ({
  sectionId,
  containerId,
  isAdminMode,
  gridSize,
  container,
}) => {
  const t = useScrollytellTranslation();
  const gridSizeRef = createRef();
  const [isDragging, setIsDragging] = useState(false);
  const [showFocusZoom, setShowFocusZoom] = useState(false);
  const { activeElement } = useScrollytellState();
  const isActiveContainer =
    activeElement[0] === sectionId &&
    activeElement[1] === containerId;

  const dispatch = useScrollytellDispatch();
  const { onChange, story } = useScrollyTellProps();

  const handleEdit = (property, value) => {
    onChange(
      updateContainer(story, sectionId, containerId, { [property]: value })
    );
  };

  const handleAddBlock = (emptyBlock) => {
    const updatedStory = addBlock(
      story,
      sectionId,
      containerId,
      emptyBlock
    );
    onChange(updatedStory);
    const blockId =
      updatedStory.sections[sectionId].containers[containerId].blocks.length - 1;
    dispatch({
      type: SET_ACTIVE_ELEMENT,
      activeSection: sectionId,
      activeContainer: containerId,
      activeBlock: blockId,
    });
  };

  const handleQuickPlacement = (placement) => {
    const isNineCol =
      gridSize === SectionGridSize.NINE_COLUMNS || gridSize === "nineColumns";
    if (isNineCol && (placement === "left" || placement === "right")) {
      const updatedContainers = (currentSection?.containers || []).map(
        (c, idx) => {
          if (idx === containerId) {
            return placement === "left"
              ? { ...c, startColumn: 2, endColumn: 6 }
              : { ...c, startColumn: 8, endColumn: 12 };
          }
          if (
            (c.startColumn === 5 && c.endColumn === 6) ||
            (c.startColumn === 4 && c.endColumn === 6)
          ) {
            return { ...c, startColumn: 4, endColumn: 10 };
          }
          if (c.startColumn === 2 && c.endColumn === 5) {
            return { ...c, startColumn: 2, endColumn: 6 };
          }
          if (c.startColumn === 6 && c.endColumn === 9) {
            return { ...c, startColumn: 8, endColumn: 12 };
          }
          return c;
        }
      );
      onChange(
        updateSection(story, sectionId, {
          ...currentSection,
          gridSize: SectionGridSize.TWELVE_COLUMNS,
          containers: updatedContainers,
        })
      );
      return;
    }
    const updated = applyStickyCardPlacement(container, placement, gridSize);
    onChange(
      updateContainer(story, sectionId, containerId, {
        startColumn: updated.startColumn,
        endColumn: updated.endColumn,
      })
    );
  };

  const handleFocusZoomChanged = (kenBurns) => {
    onChange(
      updateContainer(story, sectionId, containerId, { kenBurns })
    );
  };

  const currentSection = story?.sections?.[sectionId];
  const isMal5 =
    currentSection?.templateId === SectionTemplates.STICKY_SECTION ||
    (currentSection?.type === "sticky" &&
      currentSection?.templateId !== 6 &&
      !Array.isArray(currentSection?.backgroundImage));
  const backgroundImage = currentSection?.backgroundImage;

  const { startColumn = 5, endColumn = 6 } = container;

  const renderPanels = () => {
    if (isDragging) return null;
    return (
      <>
        <EditContainerPanel
          borderRadius={container.borderRadius}
          backgroundColor={container.backgroundColor}
          sectionId={sectionId}
          containerId={containerId}
          onEdit={handleEdit}
        />
        {isMal5 && (
          <AdminIconBar
            style={{
              position: "absolute",
              bottom: "-44px",
              left: "4px",
              zIndex: 25,
              backgroundColor: "#4484F1",
              borderRadius: "4px",
              color: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            }}
          >
            <AdminIconBarItem
              Icon={AlignHorizontalLeft}
              onClick={() => handleQuickPlacement("left")}
              tooltip={t("editStickyContainer.placeLeft", "Plasser kort til venstre")}
            />
            <AdminIconBarItem
              Icon={AlignHorizontalCenter}
              onClick={() => handleQuickPlacement("center")}
              tooltip={t("editStickyContainer.placeCenter", "Plasser kort i midten")}
            />
            <AdminIconBarItem
              Icon={AlignHorizontalRight}
              onClick={() => handleQuickPlacement("right")}
              tooltip={t("editStickyContainer.placeRight", "Plasser kort til høyre")}
            />
            {backgroundImage && (
              <AdminIconBarItem
                Icon={CenterFocusStrong}
                onClick={() => {
                  if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  setShowFocusZoom(true);
                }}
                tooltip={t("editStickyContainer.kenBurns", "Ken Burns fokus og zoom for dette steget")}
              />
            )}
          </AdminIconBar>
        )}
        <AddBlockPanel
          onAdd={handleAddBlock}
          tag={
            currentSection?.templateId === SectionTemplates.SCROLLVIDEO_SECTION
              ? "all"
              : "advanced"
          }
        />
        {currentSection?.templateId !== 6 && (
          <DuplicateContainerButton
            container={container}
            sectionId={sectionId}
            containerId={containerId}
          />
        )}
        <RemoveContainerButton
          sectionId={sectionId}
          containerId={containerId}
        />
      </>
    );
  };

  if (isAdminMode && isActiveContainer) {
    if (showFocusZoom && backgroundImage) {
      return (
        <ImageFocusZoomPicker
          imageUrl={backgroundImage}
          x={container?.kenBurns?.x ?? 0}
          y={container?.kenBurns?.y ?? 0}
          zoom={container?.kenBurns?.zoom ?? 1.0}
          effect={container?.kenBurns?.effect || "none"}
          onFocusZoomChanged={handleFocusZoomChanged}
          onClose={() => setShowFocusZoom(false)}
          sectionId={sectionId}
          containerId={containerId}
        />
      );
    }
    return (
      <>
        <GridSizeSelectors
          containerId={containerId}
          sectionId={sectionId}
          data={{ startColumn, endColumn, gridSize }}
          ref={gridSizeRef}
          onIsDraggingChange={setIsDragging}
        />
        {renderPanels()}
      </>
    );
  }
  return null;
};
